import type { TooltipProps } from "@mui/material";
import { Divider, Stack, styled, Tooltip, tooltipClasses, Typography } from "@mui/material";
import dayjs from "../dayjs";
import type { ReactElement } from "react";
import type { LoyaltyPointsHistoryItem } from "../admin/loyalty/loyaltyApi";
import { useLoyaltyPointsHistory } from "./useLoyaltyPointsHistory";

const StyledTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} arrow />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    maxWidth: 320,
    fontSize: theme.typography.body2.fontSize,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 12,
    padding: theme.spacing(1.5),
    boxShadow: theme.shadows[3],
  },
}));

function LoyaltyPointsHistoryTooltip({ children, isCustomer }: { children: ReactElement; isCustomer: boolean }) {
  const { loyaltyPointsHistory } = useLoyaltyPointsHistory(isCustomer);
  return (
    <StyledTooltip
      title={
        <Stack spacing={1}>
          {loyaltyPointsHistory.length == 0 ? (
            <NoEvents />
          ) : (
            loyaltyPointsHistory.map((event) => <LoyaltyPointsHistoryEvent event={event} key={event.id} />)
          )}
        </Stack>
      }
      arrow
      enterDelay={300}
      placement="bottom"
    >
      <span style={{ display: "inline-flex" }}>{children}</span>
    </StyledTooltip>
  );
}

function LoyaltyPointsHistoryEvent({ event }: { event: LoyaltyPointsHistoryItem }) {
  return (
    <Stack key={event.id} spacing={0.25}>
      {event.orderId && (
        <Typography variant="body2">
          <strong>Zamówienie:</strong> {event.orderId.userFriendly}
        </Typography>
      )}
      {event.rewardId && (
        <Typography variant="body2">
          <strong>Nagroda:</strong> {event.rewardId}
        </Typography>
      )}
      <Typography variant="body2">
        <strong>Punkty:</strong> {event.amount > 0 ? "+" + event.amount : event.amount}
      </Typography>
      <Typography
        variant="caption"
        sx={{
          color: "text.secondary",
        }}
      >
        {dayjs(event.createdAt).format("DD.MM.YYYY HH:mm")}
      </Typography>
      <Divider />
    </Stack>
  );
}

function NoEvents() {
  return <Typography variant="body2">Brak historii</Typography>;
}

export default LoyaltyPointsHistoryTooltip;
